import { useRef, useState } from "react";
import {
  diffLines,
  diffStats,
  type ChatTurn,
  type DiffLine,
  type RepairOutcome,
  type RepairStep,
} from "@roqeet/core";
import { aiRepair, type RepairKind } from "../../state/repair-actions.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * AI 코드 수리/생성 패널 — 결정 #30 3단계(UI). 사용자 정한 3요소를 담는다:
 *  - 대화형: 지시(instruction)를 주고, 결과가 마음에 안 들면 이어서 다시 지시(priorMessages).
 *  - 반복 캡: core 루프가 maxIterations·무진전 중단으로 토큰을 보호(여기선 결과만 표시).
 *  - diff 매번: 적용 전 항상 변경 내용을 diff로 보여줘 사용자가 확인 후 적용/취소.
 *
 * 보안(결정 #17): 적용은 단지 에디터 텍스트를 바꿀 뿐, 저장·실행 시 화이트리스트·
 * 샌드박스를 그대로 통과한다. 이 패널은 신뢰 경계가 아니다.
 */
export function AiRepairPanel({
  kind,
  value,
  onApply,
  onClose,
}: {
  kind: RepairKind;
  value: string;
  onApply: (next: string) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState<RepairStep[]>([]);
  const [outcome, setOutcome] = useState<RepairOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 대화 누적(대화형 후속 턴). 직전 결과의 messages를 이어붙인다.
  const priorMessages = useRef<ChatTurn[] | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  useEscapeToClose(onClose, !busy);

  const run = async () => {
    setBusy(true);
    setError(null);
    setSteps([]);
    setOutcome(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const result = await aiRepair({
        kind,
        source: value,
        ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        ...(priorMessages.current ? { priorMessages: priorMessages.current } : {}),
        signal: ctrl.signal,
        onStep: (s) => setSteps((prev) => [...prev, s]),
      });
      setOutcome(result);
      priorMessages.current = result.messages; // 후속 턴 연결
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const cancel = () => abortRef.current?.abort();

  const changed = outcome && outcome.source !== value;
  const diff = changed ? diffLines(value, outcome!.source) : null;
  const stats = diff ? diffStats(diff) : null;

  return (
    <div
      className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      style={{ zIndex: 70 }}
      onClick={onClose}
    >
      <div
        className="flex max-h-screen max-h-[100dvh] w-full max-w-lg flex-col overflow-hidden bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-line"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <span className="text-sm font-medium text-ink">{t("repair.title")}</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line px-3 py-1 text-sm text-ink-soft hover:bg-bg-raised"
          >
            {t("misc.close")}
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 scroll-touch">
        {/* 지시 입력(대화형) */}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-soft">
            {t("repair.request")}
          </label>
          <textarea
            className="w-full resize-none rounded-lg border border-line bg-bg-soft p-3 text-ink outline-none focus:border-accent"
            style={{ fontSize: 16, minHeight: 72 }}
            placeholder={
              kind === "rqg-json"
                ? t("repair.phGame")
                : t("repair.phScript")
            }
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={busy}
          />
          <div className="mt-2 flex gap-2">
            {busy ? (
              <button
                type="button"
                onClick={cancel}
                className="rounded-lg border border-line px-3 py-1.5 text-sm text-red-300 hover:bg-bg-raised"
              >
                {t("repair.stop")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void run()}
                className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
              >
                {priorMessages.current ? t("repair.runAgain") : t("repair.run")}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        {/* 진행 단계(타임라인) */}
        {steps.length > 0 && (
          <div className="rounded-lg border border-line bg-bg-soft p-2">
            <div className="mb-1 text-xs font-semibold text-ink-faint">{t("repair.progress")}</div>
            <ul className="space-y-0.5 text-xs text-ink-soft">
              {steps.map((s, i) => (
                <li key={i}>
                  {phaseLabel(s, t)}
                  {s.errors.length > 0 && (
                    <span className="text-amber-300">
                      {" · "}{t("scriptTest.errorCount", { count: s.errors.length })}
                    </span>
                  )}
                  {s.note && <span className="text-ink-faint"> · {s.note}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 결과 + diff */}
        {outcome && (
          <div className="space-y-2">
            <div
              className={[
                "rounded-lg px-3 py-2 text-xs",
                outcome.ok ? "bg-bg-soft text-ink-soft" : "bg-amber-500/10 text-amber-300",
              ].join(" ")}
            >
              {outcomeLabel(outcome, t)}
              {outcome.remainingErrors.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {outcome.remainingErrors.slice(0, 5).map((e, i) => (
                    <li key={i}>· {e}</li>
                  ))}
                </ul>
              )}
            </div>

            {!changed ? (
              <p className="text-xs text-ink-faint">{t("misc.noChanges")}</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-faint">
                    {t("misc.diffPreview")}{" "}
                    <span className="text-green-400">+{stats!.added}</span>{" "}
                    <span className="text-red-400">-{stats!.removed}</span>
                  </span>
                </div>
                <DiffView diff={diff!} />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onApply(outcome.source);
                      onClose();
                    }}
                    className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
                  >
                    {t("common.apply")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOutcome(null)}
                    className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
                <p className="text-xs text-ink-faint">
                  {t("repair.reviewHint")}
                </p>
              </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function DiffView({ diff }: { diff: DiffLine[] }) {
  return (
    <pre className="max-h-72 overflow-auto rounded-lg border border-line bg-bg-soft p-2 font-mono text-xs leading-relaxed">
      {diff.map((d, i) => (
        <div
          key={i}
          className={
            d.op === "add"
              ? "bg-green-500/10 text-green-300"
              : d.op === "remove"
                ? "bg-red-500/10 text-red-300"
                : "text-ink-soft"
          }
        >
          <span className="select-none text-ink-faint">
            {d.op === "add" ? "+ " : d.op === "remove" ? "- " : "  "}
          </span>
          {d.text || "\u00a0"}
        </div>
      ))}
    </pre>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function phaseLabel(s: RepairStep, t: Translate): string {
  const it = s.iteration > 0 ? `#${s.iteration} ` : "";
  switch (s.phase) {
    case "pre-pass":
      return `${it}${t("scriptTest.phasePrePass")}`;
    case "validate":
      return `${it}${t("repair.phaseValidate")}`;
    case "ai":
      return `${it}${t("scriptTest.phaseAi")}`;
    case "aborted":
      return `${it}${t("scriptTest.phaseAborted")}`;
    case "error":
      return `${it}${t("scriptTest.phaseError")}`;
    default:
      return `${it}${s.phase}`;
  }
}

function outcomeLabel(o: RepairOutcome, t: Translate): string {
  switch (o.stoppedReason) {
    case "valid":
      return o.ok ? t("repair.outcomeValid", { count: o.iterations }) : t("repair.done");
    case "max-iterations":
      return t("repair.outcomeMax", { count: o.iterations });
    case "no-progress":
      return t("repair.outcomeNoProgress");
    case "aborted":
      return t("scriptTest.stopped");
    case "error":
      return t("repair.outcomeError");
    default:
      return t("repair.done");
  }
}
